import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Tooltip from '../components/ui/Tooltip'
import { api } from '../services/api'
import { dashboardService, REPORTING_PERIODS } from '../services/dashboardService'
import { buildQuery } from '../services/crmService'

const SECTION_TITLES = {
    subscriber: 'Subscriber Performance',
    usage: 'Usage and Matching',
    revenue: 'Revenue',
}

const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/

/** e.g. "02 Oct 2026, 14:35" in Europe/London (reporting timezone). */
function formatLondonDateTime(value) {
    const d = new Date(value)
    if (isNaN(d.getTime())) return String(value)
    const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Europe/London' })
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/London' })
    return `${date}, ${time}`
}

function formatMetricValue(value, currency) {
    if (value == null) return '-'
    if (typeof value === 'string' && ISO_DATETIME.test(value)) return formatLondonDateTime(value)
    if (typeof value === 'number') {
        return currency
            ? new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value)
            : value.toLocaleString()
    }
    return String(value)
}

/** Management metrics grouped by section; renders whatever keys the API returns. */
function ManagementMetrics() {
    const navigate = useNavigate()
    const [period, setPeriod] = useState('30d')
    const [state, setState] = useState({ data: null, loading: true, error: null })

    useEffect(() => {
        let cancelled = false
        setState((prev) => ({ ...prev, loading: true, error: null }))
        dashboardService.getDashboard(period)
            .then((res) => {
                if (cancelled) return
                if (!res.success) throw new Error('Failed to load metrics')
                setState({ data: res.data, loading: false, error: null })
            })
            .catch((err) => {
                if (!cancelled) setState({ data: null, loading: false, error: err.message || 'Failed to load metrics' })
            })
        return () => { cancelled = true }
    }, [period])

    const sections = state.data
        ? Object.entries(state.data).filter(([key, val]) => key !== 'period' && val && typeof val === 'object')
        : []

    const openCrm = (filter) => navigate(`/crm${buildQuery({ period, ...filter })}`)

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-admin-muted">
                    Current-state figures are always live; period figures use {state.data?.period?.timezone || 'Europe/London'} time.
                </p>
                <label className="flex items-center gap-2 text-sm text-admin-muted">
                    <span className="font-bold">Reporting period</span>
                    <select
                        className="select-admin font-bold"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        aria-label="Reporting period"
                    >
                        {REPORTING_PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                </label>
            </div>

            {state.error && (
                <div role="alert" className="p-3 text-sm rounded-md bg-red-50 text-status-error border border-red-200">
                    {state.error}
                </div>
            )}
            {state.loading && !state.data && <p className="text-xs text-admin-muted">Loading metrics...</p>}

            {sections.map(([key, section]) => {
                const { currency, ...metrics } = section
                const tiles = Object.entries(metrics).filter(([, v]) => v && typeof v === 'object' && 'label' in v)
                return (
                    <section key={key} aria-label={SECTION_TITLES[key] || key} className={state.loading ? 'opacity-60' : ''}>
                        <div className="flex items-center gap-3 mb-3">
                            <h3 className="text-sm font-bold text-admin-muted uppercase tracking-wider">{SECTION_TITLES[key] || key}</h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {tiles.map(([k, metric]) => (
                                <MetricCard
                                    key={k}
                                    label={metric.label}
                                    value={formatMetricValue(metric.value, key === 'revenue' ? currency : null)}
                                    definition={metric.definition}
                                    onClick={metric.crmFilter ? () => openCrm(metric.crmFilter) : null}
                                />
                            ))}
                        </div>
                    </section>
                )
            })}
        </div>
    )
}

/**
 * Dashboard Page - Enterprise Standard
 * Minimalist, data-dense, functional.
 */
export default function Dashboard() {
    const [data, setData] = useState({
        stats: null,
        recentJobs: [],
        auditLogs: [],
        loading: true,
        error: null,
    })

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [statsRes, logsRes, auditRes] = await Promise.all([
                    api.get('/admin/stats'),
                    api.get('/admin/ingestion-logs'),
                    api.get('/admin/audit-log')
                ])

                setData({
                    stats: statsRes.success ? statsRes.data : null,
                    recentJobs: logsRes.success ? logsRes.data.slice(0, 5) : [],
                    auditLogs: auditRes.success ? auditRes.data.slice(0, 5) : [],
                    loading: false,
                    error: null
                })
            } catch (err) {
                console.error('Failed to load dashboard:', err)
                setData(prev => ({ ...prev, loading: false, error: 'Dashboard failed to load live data.' }))
            }
        }
        fetchDashboardData()
    }, [])

    const statsConfig = [
        {
            label: 'Registered Customer Users',
            value: data.stats?.users != null ? data.stats.users.toLocaleString() : '-',
            trend: 'Live',
            trendPositive: true,
            definition: "Active user accounts belonging to customer organisations. Internal / test accounts are excluded."
        },
        {
            label: 'Active Subscriptions (incl. trials)',
            value: data.stats?.activeSubscriptions != null ? data.stats.activeSubscriptions.toLocaleString() : '-',
            trend: data.stats?.activePayingOrganisations != null
                ? `${data.stats.activePayingOrganisations} paying · ${data.stats.activeTrials} on trial`
                : 'Live',
            trendPositive: true,
            definition: 'Customer organisations with a live subscription — paying plus active trials (the split is shown beneath). Active Paying Organisations and Active Trials are also reported separately in the metrics above. Internal / test accounts are excluded.'
        },
        {
            label: 'Tenders Ingested',
            value: data.stats?.tenders != null ? data.stats.tenders.toLocaleString() : '-',
            trend: 'Live',
            trendPositive: true,
            definition: "Tenders ingested, normalised, and stored by the system (not bids submitted)."
        },
        {
            label: 'Customer Organisations',
            value: data.stats?.organisations != null ? data.stats.organisations.toLocaleString() : '-',
            trend: 'Live',
            trendPositive: true,
            alert: false,
            definition: "Registered customer organisations (excluding confirmed deletions and internal / test accounts)."
        },
        {
            label: 'Active Tenders',
            value: data.stats?.activeTenders != null ? data.stats.activeTenders.toLocaleString() : '-',
            trend: 'Live',
            trendPositive: true,
            definition: "Tenders currently open, with a closing date in the future or unspecified."
        },
        {
            label: 'Expired Tenders',
            value: data.stats?.expiredTenders != null ? data.stats.expiredTenders.toLocaleString() : '-',
            trend: 'Live',
            trendPositive: false,
            definition: "Tenders whose closing date has passed."
        },
    ]

    return (
        <div className="space-y-6 animate-fade-in">
            <div>
                <h1 className="text-2xl font-bold text-admin-heading tracking-tight">Admin Dashboard</h1>
                <p className="text-sm text-admin-muted mt-1">Subscribers, usage, revenue and platform health at a glance.</p>
            </div>

            <ManagementMetrics />

            <h2 className="text-sm font-bold text-admin-muted uppercase tracking-wider">Platform Overview</h2>
            {/* Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {statsConfig.map((stat) => (
                    <MetricCard key={stat.label} {...stat} />
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Ingestion Status */}
                <div className="card-admin p-6">
                    <h2 className="text-lg font-bold mb-4">Ingestion Status</h2>
                    <div className="overflow-hidden">
                        <table className="min-w-full text-left text-sm whitespace-nowrap">
                            <thead className="border-b border-admin-border text-admin-muted">
                                <tr>
                                    <th className="pb-3 font-bold uppercase text-xs tracking-wider">Source</th>
                                    <th className="pb-3 font-bold uppercase text-xs tracking-wider">Tenders</th>
                                    <th className="pb-3 font-bold uppercase text-xs tracking-wider">Status</th>
                                    <th className="pb-3 font-bold uppercase text-xs tracking-wider text-right">Time</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.loading ? (
                                    <tr><td colSpan={4} className="py-4 text-xs text-admin-muted">Loading ingestion logs...</td></tr>
                                ) : data.recentJobs.length === 0 ? (
                                    <tr><td colSpan={4} className="py-4 text-xs text-admin-muted">No recent jobs found.</td></tr>
                                ) : (
                                    data.recentJobs.map((job) => (
                                        <TableRow key={job.id} job={job} />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* System Health / Audit */}
                <div className="card-admin p-6">
                    <h2 className="text-lg font-bold">Admin Panel Activity (Audit Log)</h2>
                    <p className="text-xs text-admin-muted mb-4">Actions taken by administrators in this Admin Panel. Customer platform logins are reported separately in the dashboard metrics.</p>
                    <div className="space-y-4">
                        {data.loading ? (
                            <p className="text-xs text-admin-muted">Loading audit log...</p>
                        ) : data.auditLogs.length === 0 ? (
                            <p className="text-xs text-admin-muted">No recent audit logs.</p>
                        ) : (
                            data.auditLogs.map((audit) => (
                                <AuditRow
                                    key={audit.id}
                                    actor={audit.actor?.email || 'System'}
                                    action={audit.action}
                                    record={audit.record}
                                    details={audit.details}
                                    time={new Date(audit.createdAt).toLocaleDateString('en-GB') + ' ' + new Date(audit.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                />
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

function MetricCard({ label, value, trend, trendPositive, alert, definition, onClick }) {
    const Wrapper = onClick ? 'button' : 'div'
    return (
        <Wrapper
            type={onClick ? 'button' : undefined}
            onClick={onClick || undefined}
            aria-label={onClick ? `${label}: ${value}. View in CRM` : undefined}
            className={`card-admin p-5 flex flex-col justify-between h-32 text-left w-full ${onClick ? 'cursor-pointer hover:border-brand-navy hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy transition' : ''}`}
        >
            <div>
                <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-bold text-admin-muted uppercase tracking-wider">{label}</p>
                    {definition && (
                        <Tooltip text={definition}>
                            <svg className="w-4 h-4 text-admin-muted/60 hover:text-brand-navy transition-colors cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </Tooltip>
                    )}
                </div>
                <p className="text-3xl font-bold text-brand-navy mt-2 tracking-tight">{value}</p>
            </div>
            {trend ? (
                <div className={`text-xs font-bold flex items-center gap-1 ${alert ? 'text-status-error' : (trendPositive ? 'text-status-success' : 'text-admin-muted')}`}>
                    {alert && <span className="w-1.5 h-1.5 rounded-full bg-status-error inline-block mr-1"></span>}
                    {trend}
                </div>
            ) : onClick ? (
                <div className="text-xs font-bold text-brand-navy">View in CRM →</div>
            ) : null}
        </Wrapper>
    )
}

function TableRow({ job }) {
    const statusBadges = {
        completed: <span className="badge badge-success">Success</span>,
        failed: <span className="badge badge-error">Failed</span>,
        running: <span className="badge badge-pending">Running</span>,
        idle: <span className="badge badge-neutral">Idle</span>,
    }
    const { source, records_new, status, started_at } = job

    // Map existing records to the display text
    const tendersCount = records_new || 0
    const timeDisplay = started_at ? new Date(started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'Unknown'
    const statusDisplay = statusBadges[status] || statusBadges['completed'] // generic fallback

    return (
        <tr>
            <td className="py-3 font-bold text-brand-navy">{source}</td>
            <td className="py-3 text-admin-text font-semibold">{tendersCount}</td>
            <td className="py-3">{statusDisplay}</td>
            <td className="py-3 text-right text-admin-muted text-xs font-mono">{timeDisplay}</td>
        </tr>
    )
}

function AuditRow({ actor, action, record, details, time }) {
    const [open, setOpen] = useState(false)
    const recordText = record ? [record.type, record.label].filter(Boolean).join(': ') : null
    const hasDetails = details && Object.keys(details).length > 0
    return (
        <div className="flex items-start gap-3">
            <div className="w-8 h-8 shrink-0 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-admin-muted border border-admin-border">
                {actor.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
                <p className="text-sm font-bold text-brand-navy">{action}</p>
                {recordText && <p className="text-xs text-brand-navy break-words">{recordText}</p>}
                <p className="text-xs text-admin-muted">{actor} • {time}</p>
                {hasDetails && (
                    <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs text-admin-muted underline mt-0.5" aria-expanded={open}>
                        {open ? 'Hide details' : 'Show details'}
                    </button>
                )}
                {open && hasDetails && (
                    <pre className="mt-1 text-[11px] bg-slate-50 border border-admin-border rounded p-2 whitespace-pre-wrap break-words">{JSON.stringify(details, null, 2)}</pre>
                )}
            </div>
        </div>
    )
}
